// AssistantFeedbackPanel — the Admin review of thumbs up/down on Harvi answers.
//
// Reads GET /api/assistant/feedback. Each row is one person's rating of one
// answer, with the question behind it, the tools the answer used and the prompt
// version it ran under — so "did v15 get worse" is answerable from this screen.
//
// "Export as eval cases" downloads the same rows as case skeletons for
// evals/harvi/cases.json. Each is marked needsReview: a thumbs-down says an
// answer was wrong, not what the right answer is, so a person fills `expect`.
import { useState } from 'react';
import { B } from '../../constants/brand';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Btn, Spinner, ErrorMsg, Card, Badge, Select, Icon } from '../ui';

const RATING_OPTIONS = [
  { value: '', label: 'All ratings' },
  { value: 'down', label: 'Thumbs down' },
  { value: 'up', label: 'Thumbs up' },
];

function downloadJson(name, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const clip = (s, n) => (s && s.length > n ? `${s.slice(0, n)}…` : s || '');

export const AssistantFeedbackPanel = () => {
  const [rating, setRating] = useState('down');
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState(null);
  const qs = `assistant/feedback?limit=100${rating ? `&rating=${rating}` : ''}`;
  const { data, loading, error } = useApi(() => api.get(qs), [qs]);

  const exportCases = async () => {
    setExporting(true); setExportErr(null);
    try {
      const res = await api.get(`${qs}&format=eval`);
      downloadJson(`harvi-eval-candidates-${new Date().toISOString().slice(0, 10)}.json`, { cases: res.cases || [] });
    } catch (e) { setExportErr(e.message); }
    finally { setExporting(false); }
  };

  const items = data?.items || [];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '28px 0 4px', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text }}>Harvi answer ratings</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select value={rating} onChange={setRating} options={RATING_OPTIONS} />
          <Btn size="sm" onClick={exportCases} disabled={exporting || !items.length}>
            <Icon name="download" size={14} /> {exporting ? 'Exporting…' : 'Export as eval cases'}
          </Btn>
        </div>
      </div>
      <div style={{ fontSize: 13, color: B.textMd, marginBottom: 14 }}>
        Thumbs up and down people gave Harvi's answers, newest first. Exported cases need review before they go in
        evals/harvi/cases.json — a thumbs-down says an answer was wrong, not what the right one is.
      </div>
      {exportErr && <ErrorMsg msg={exportErr} />}
      {data?.byPromptVersion?.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          {data.byPromptVersion.map(v => (
            <Badge key={v.promptVersion} color={B.textMd}>{v.promptVersion}: 👍 {v.up} · 👎 {v.down}</Badge>
          ))}
        </div>
      )}
      <Card style={{ padding: 0 }}>
        {loading ? <div style={{ padding: 20 }}><Spinner /></div>
          : error ? <div style={{ padding: 20 }}><ErrorMsg msg={error} /></div>
          : !items.length ? (
            <div style={{ padding: 20, fontSize: 13, color: B.textMd }}>No ratings yet.</div>
          ) : items.map((it) => {
            const isOpen = open === it.feedbackId;
            return (
              <div key={it.feedbackId} style={{ borderBottom: `1px solid ${B.border}`, padding: '12px 16px' }}>
                <button type="button" onClick={() => setOpen(isOpen ? null : it.feedbackId)}
                  style={{ all: 'unset', cursor: 'pointer', display: 'block', width: '100%' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span>{it.rating === 1 ? '👍' : '👎'}</span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{clip(it.question, 140) || '(no question)'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: B.textLt, marginTop: 2 }}>
                    {it.raterName || 'Someone'} · {it.ratedAt ? new Date(it.ratedAt).toLocaleString() : ''}
                    {it.promptVersion ? ` · ${it.promptVersion}` : ''}{it.tools?.length ? ` · ${it.tools.join(', ')}` : ''}
                  </div>
                  {it.comment && <div style={{ fontSize: 13, color: B.textMd, marginTop: 4 }}>“{it.comment}”</div>}
                </button>
                {isOpen && (
                  <div style={{ marginTop: 8, fontSize: 13, color: B.text, whiteSpace: 'pre-wrap', background: B.surfaceMuted,
                                border: `1px solid ${B.divider}`, borderRadius: 8, padding: 10 }}>
                    {it.answer || '(empty answer)'}
                  </div>
                )}
              </div>
            );
          })}
      </Card>
      {data && data.total > items.length && (
        <div style={{ fontSize: 12, color: B.textLt, marginTop: 6 }}>Showing {items.length} of {data.total}.</div>
      )}
    </div>
  );
};

export default AssistantFeedbackPanel;
